import { useMemo } from "react";

import { cn } from "@/lib/utils";
import { useTranslation } from "@/i18n";
import type { TranslationKey } from "@/i18n";

export type ObservabilityTab =
  | "overview"
  | "inference"
  | "policy"
  | "audit";

interface ObservabilityTabsProps {
  value: ObservabilityTab;
  onChange: (value: ObservabilityTab) => void;
}

// Each tab maps to a translation key so the tab strip re-renders
// on a locale switch.
const TAB_KEY: Record<ObservabilityTab, TranslationKey> = {
  overview: "observability.tabs.overview",
  inference: "observability.tabs.inference",
  policy: "observability.tabs.policy",
  audit: "observability.tabs.audit",
};

export function ObservabilityTabs({
  value,
  onChange,
}: ObservabilityTabsProps) {
  const { t } = useTranslation();

  const options = useMemo(
    () =>
      (
        ["overview", "inference", "policy", "audit"] as const
      ).map((tab) => ({ value: tab, label: t(TAB_KEY[tab]) })),
    [t]
  );

  return (
    <div
      role="tablist"
      className="flex gap-1 overflow-x-auto border-b border-ocean-100"
    >
      {options.map((opt) => {
        const isActive = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(opt.value)}
            className={cn(
              "shrink-0 border-b-2 px-4 py-2 text-sm font-medium transition-colors",
              isActive
                ? "border-ocean-600 text-ocean-900"
                : "border-transparent text-neutral-500 hover:text-ocean-800"
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
